<template>
  <div>
    <!-- HERO -->
    <BaseHero :media_id="media_id" :overlay="false">
      <div class="d-flex align-end align-lg-center h-100">
        <div class="hero-text-container">
          <h1 class="text-h1 text-white text-center text-sm-left">
            {{
              isIndonesia()
                ? getContent(media_id, "hero")?.title_ind
                : getContent(media_id, "hero")?.title_eng
            }}
          </h1>

          <!-- myCustomer Service Button -->
          <button
            id="home-faq-open-btn"
            class="faq-hero-btn mt-6 text-body-1"
            @click="openFaqPopup"
          >
            <template v-if="isIndonesia()">
              Temukan informasi lainnya dengan
              <strong>myCustomer Service</strong>
            </template>
            <template v-else>
              Discover more with <strong>myCustomer Service</strong>
            </template>
            <v-icon size="20" class="ml-1">mdi-arrow-right</v-icon>
          </button>
        </div>
      </div>
    </BaseHero>

    <!-- FAQ POPUP -->
    <LazyHomeFaqPopup
      v-model="faqDialog"
      :search="faqSearch"
      :loading="faqLoading"
      :active-topic="faqActiveTopic"
      :active-article="faqActiveArticle"
      @update:search="faqSearch = $event"
      @close="closeFaqPopup"
      @do-search="doFaqSearch"
      @select-topic="selectFaqTopic"
      @clear-topic="faqActiveTopic = null"
      @open-article="openFaqArticle"
      @clear-article="faqActiveArticle = null"
    />

    <!-- SLIDESHOW -->
    <UseElementVisibility v-slot="{ isVisible }" style="height: 200px">
      <v-container style="transform: translateY(-50%)" v-show="isVisible">
        <v-carousel
          v-if="sliders?.length != 0"
          v-model="currentSlide"
          show-arrows="hover"
          cycle
          hide-delimiters
          height="auto"
        >
          <!-- Image Carousel -->
          <v-carousel-item v-for="(slider, idx) in sliders" :key="idx">
            <div class="d-flex align-center">
              <NuxtImg
                v-if="loadedSlides[idx]"
                :src="slider?.image_path"
                :alt="slider?.image_desc"
                sizes="100vw md:50vw"
                class="rounded-xl image-carousel"
                style="margin-right: 4%"
                :class="{ 'cursor-pointer': isClickable(slider) }"
                loading="lazy"
                decoding="async"
                format="webp"
                quality="80"
                @click="handleSliderLink(slider)"
              />
              <div
                v-else
                class="rounded-xl image-carousel bg-grey-lighten-3"
                style="margin-right: 4%"
              ></div>

              <NuxtImg
                v-if="loadedSlides[idx]"
                :src="sliders[(idx + 1) % sliders.length]?.image_path"
                :alt="sliders[(idx + 1) % sliders.length]?.image_desc"
                sizes="100vw md:50vw"
                class="rounded-xl image-carousel d-none d-md-inline-block"
                :class="{ 'cursor-pointer': isClickable(nextSlider(idx)) }"
                loading="lazy"
                decoding="async"
                format="webp"
                quality="80"
                @click="handleSliderLink(nextSlider(idx))"
              />
              <div
                v-else
                class="rounded-xl image-carousel bg-grey-lighten-3 d-none d-md-inline-block"
              ></div>
            </div>
          </v-carousel-item>
        </v-carousel>
      </v-container>
    </UseElementVisibility>

    <!-- ABOUT US -->
    <v-container>
      <v-row
        style="padding: 5% 0 10% 0"
        class="d-flex align-center about-container"
      >
        <v-col
          cols="12"
          lg="6"
          class="d-flex flex-column justify-center pr-lg-8"
        >
          <div class="text-h6 text-secondary font-weight-medium text-uppercase">
            {{ lang("homeAboutCaption") }}
          </div>
          <h2 class="text-h2 text-primary-darken-2 mb-6">
            {{ lang("homeAboutTitle") }}
          </h2>
          <p class="text-body-1 text-justify text-text-subdued mb-6">
            {{ lang("homeAboutDesc") }}
          </p>

          <!-- CTA -->
          <router-link
            class="text-decoration-none"
            :to="isIndonesia() ? '/id/tentang-spil' : '/en/about-spil'"
            @click="
              () => {
                setActivePage(
                  isIndonesia() ? '/id/tentang-spil' : '/en/about-spil',
                );

                const params = {
                  action: 'CLICK CTA',
                  remark: 'Click About Us CTA',
                  val1: null,
                  val2: null,
                  val3: null,
                  val4: null,
                  val5: null,
                };

                saveTrackAnalytics(params);
              }
            "
          >
            <BaseButton>{{ lang("homeAboutCTA") }}</BaseButton>
          </router-link>
        </v-col>

        <v-col cols="12" lg="6" class="pl-lg-8">
          <img
            :src="AboutUsImg"
            alt="shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL"
            loading="lazy"
            decoding="async"
            class="rounded-lg"
            style="
              width: 100%;
              height: auto;
              aspect-ratio: 1.5;
              object-fit: cover;
            "
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- DATA -->
    <div class="relative d-flex align-center">
      <!-- Background -->
      <img
        :src="DataBgImg"
        alt="shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL"
        loading="lazy"
        decoding="async"
        style="width: 100%; object-fit: cover"
        class="ov-bg"
      />

      <!-- Content -->
      <div class="absolute w-100">
        <v-container>
          <h2 class="text-h2 text-white mb-12 data-title">
            {{ lang("homeDataTitle") }}
          </h2>

          <div
            v-for="(ov, idx) in overview"
            :key="idx"
            class="text-center d-inline-block mr-lg-12 mr-sm-4 mb-4 overview-item"
          >
            <div class="d-flex flex-column">
              <div
                v-if="ov.hasOwnProperty('initial')"
                class="text-h1 text-white mb-4 ov-value"
              >
                <ClientOnly>
                  <UseElementVisibility v-slot="{ isVisible }">
                    <NumberAnimation
                      ref="numberAnimation"
                      v-if="isVisible"
                      :from="ov.initial"
                      :to="ov.value"
                      :format="
                        (value) =>
                          idx == 2
                            ? parseInt(value).toLocaleString('id-ID')
                            : parseInt(value)
                      "
                      :duration="4"
                      @complete="playAnimation"
                      autoplay
                      easing="linear"
                    />
                  </UseElementVisibility>
                </ClientOnly>
              </div>
              <div v-else class="text-h1 text-white mb-4 ov-value">
                {{ ov.value }}
              </div>
              <div class="text-h6 text-primary-light-1">
                {{ lang(ov.label) }}
              </div>
            </div>
          </div>
        </v-container>
      </div>
    </div>

    <!-- SERVICES -->
    <v-container>
      <div style="padding: 10% 0 5% 0">
        <div class="d-flex align-end mb-12 service-container">
          <div class="flex-grow-1">
            <div
              class="text-h6 text-secondary font-weight-medium text-uppercase"
            >
              {{ lang("homeServiceCaption") }}
            </div>
            <h2 class="text-h3 text-primary-darken-2">
              {{ lang("homeServiceTitle") }}
            </h2>
            <div class="text-body-1 text-text-subdued">
              {{ lang("homeServiceSubtitle") }}
            </div>
          </div>

          <router-link
            :to="isIndonesia() ? '/id/layanan' : '/en/services'"
            @click="
              () => {
                setActivePage(isIndonesia() ? '/id/layanan' : '/en/services');

                const params = {
                  action: 'CLICK CTA',
                  remark: 'Click Our Services CTA',
                  val1: null,
                  val2: null,
                  val3: null,
                  val4: null,
                  val5: null,
                };

                saveTrackAnalytics(params);
              }
            "
            class="text-decoration-none text-white d-none d-sm-block"
          >
            <BaseButton>
              {{ lang("homeServiceCTA") }}
            </BaseButton>
          </router-link>
        </div>

        <v-row>
          <v-col
            v-for="(service, idx) in services"
            :key="idx"
            cols="12"
            sm="6"
            lg="3"
            class="d-flex justify-center mb-16"
          >
            <BaseItemCard :item="service" />
          </v-col>
        </v-row>

        <router-link
          :to="isIndonesia() ? '/id/layanan' : '/en/services'"
          @click="
            () => setActivePage(isIndonesia() ? '/id/layanan' : '/en/services')
          "
          class="text-decoration-none text-white d-block d-sm-none text-center mb-12"
        >
          <BaseButton>{{ lang("homeServiceCTA") }}</BaseButton>
        </router-link>
      </div>

      <!-- NEWS -->
      <div style="padding: 5% 0">
        <div class="d-flex align-end mb-12">
          <div class="flex-grow-1">
            <div
              class="text-h6 text-secondary font-weight-medium text-uppercase"
            >
              {{ lang("homeNewsCaption") }}
            </div>
            <h2 class="text-h3 text-primary-darken-2">
              {{ lang("homeNewsTitle") }}
            </h2>
          </div>

          <router-link
            :to="isIndonesia() ? '/id/berita' : '/en/news'"
            @click="
              () => {
                setActivePage(isIndonesia() ? '/id/berita' : '/en/news');

                const params = {
                  action: 'CLICK CTA',
                  remark: 'Click Our Activities CTA',
                  val1: null,
                  val2: null,
                  val3: null,
                  val4: null,
                  val5: null,
                };

                saveTrackAnalytics(params);
              }
            "
            class="text-decoration-none text-white d-none d-sm-block"
          >
            <BaseButton>
              {{ lang("homeNewsCTA") }}
            </BaseButton>
          </router-link>
        </div>

        <v-row>
          <v-col cols="4" class="d-none d-lg-block">
            <HomeNewsSideCard :news="news_home[0]" />
          </v-col>

          <v-col
            v-if="news_home.length != 0"
            cols="12"
            lg="8"
            class="d-flex flex-column justify-space-around"
          >
            <div
              v-for="(nw, idx) in news_home.slice(breakpoint.lgAndUp ? 1 : 0)"
              :key="idx"
            >
              <router-link
                :to="
                  isIndonesia()
                    ? `/id/berita/${nw.slug_id}`
                    : `/en/news/${nw.slug_en}`
                "
                @click="handleNews(nw)"
                class="text-decoration-none text-text-default"
              >
                <HomeNewsCard :news="nw" class="mb-8 mb-lg-0" />
              </router-link>
            </div>
          </v-col>

          <v-col
            v-else
            cols="12"
            lg="7"
            class="d-flex flex-column justify-space-around"
          >
            <div v-for="(i, idx) in 3" :key="idx">
              <HomeNewsCard />
            </div>
          </v-col>
        </v-row>

        <router-link
          :to="isIndonesia() ? '/id/berita' : '/en/news'"
          @click="
            () => {
              setActivePage(isIndonesia() ? '/id/berita' : '/en/news');

              const params = {
                action: 'CLICK CTA',
                remark: 'Click Our Activities CTA',
                val1: null,
                val2: null,
                val3: null,
                val4: null,
                val5: null,
              };

              saveTrackAnalytics(params);
            }
          "
          class="text-decoration-none text-white d-block d-sm-none text-center mb-12"
        >
          <BaseButton>{{ lang("homeNewsCTA") }}</BaseButton>
        </router-link>
      </div>

      <!-- SOCIAL MEDIA -->
      <div style="padding: 5% 0">
        <div class="d-flex align-end mb-12">
          <div class="flex-grow-1">
            <div
              class="text-h6 text-secondary font-weight-medium text-uppercase"
            >
              {{ lang("homeSocmedCaption") }}
            </div>
            <h2 class="text-h3 text-primary-darken-2">
              {{ lang("homeSocmedTitle") }}
            </h2>
          </div>
        </div>

        <div class="d-flex ga-4 overflow-auto">
          <div
            v-for="sm in socmed"
            :key="sm.socmed_id"
            style="flex: 0 0 auto"
            :style="
              breakpoint.mdAndUp
                ? { width: '390px', height: '500px' }
                : { width: '100%', height: '500px' }
            "
          >
            <div v-html="sm.embed_code" style="height: 100%"></div>
          </div>
        </div>
      </div>

      <!-- BOOK NOW -->
      <div
        class="bg-primary-darken-1 rounded-xl d-flex flex-column align-center px-6 px-sm-16"
        style="margin: 10% 0"
      >
        <div class="w-100 d-lg-flex align-end justify-space-between mt-14 mb-8">
          <div class="text-h2 pr-lg-4 mb-4">
            {{ lang("homeBook")[0] }} <br />{{ lang("homeBook")[1] }}
          </div>
          <a
            :href="mySPILURL"
            target="_blank"
            class="text-decoration-none"
            @click="
              () => {
                const params = {
                  action: 'CLICK CTA',
                  remark: 'Click Book Now CTA',
                  val1: null,
                  val2: null,
                  val3: mySPILURL,
                  val4: null,
                  val5: null,
                };

                saveTrackAnalytics(params);
              }
            "
          >
            <BaseButton invert>
              {{ lang("homeBookCTA") }}
            </BaseButton>
          </a>
        </div>
        <img
          :src="HomeCtaImg"
          alt="shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL"
          class="cta-image"
          loading="lazy"
          decoding="async"
        />
      </div>
    </v-container>
  </div>
</template>

<script>
import portToPortIcon from "@/assets/images/home/services/port_to_port.svg";
import doorToDoorIcon from "@/assets/images/home/services/door_to_door.svg";
import airFreightIcon from "@/assets/images/home/services/air_freight.svg";
import landTransportIcon from "@/assets/images/home/services/land_transport.svg";
import LCLIcon from "@/assets/images/home/services/LCL.svg";
import internationalIcon from "@/assets/images/home/services/international.svg";
import asuransiIcon from "@/assets/images/home/services/asuransi.svg";
import warehouseingIcon from "@/assets/images/home/services/warehousing.svg";

import Campaign1 from "@/assets/images/home/carousel/Campaign 1.webp";
import Campaign2 from "@/assets/images/home/carousel/Campaign 2.webp";
import Campaign4 from "@/assets/images/home/carousel/Campaign 4.webp";
import Campaign5 from "@/assets/images/home/carousel/Campaign 5.webp";

import AboutUsImg from "@/assets/images/home/About Us.webp";
import DataBgImg from "@/assets/images/home/Data Background.webp";
import HomeCtaImg from "@/assets/images/home/homeCTA.webp";

import { mapActions, mapState } from "pinia";
import { useGeneralStore } from "@/stores/GeneralStore";
import { useArticleStore } from "@/stores/ArticleStore";
import { useMediaStore } from "@/stores/MediaStore";
import { useSocmedStore } from "@/stores/SocmedStore";
import { useTrackAnalyticsStore } from "@/stores/TrackAnalyticsStore";
import { useGuideStore } from "@/stores/GuideStore";

import { useDisplay } from "vuetify";
import { UseElementVisibility } from "@vueuse/components";
import { HomeNewsCard, HomeNewsSideCard } from "#components";
import NumberAnimation from "vue-number-animation";
import { isIndonesia } from "@/utils/language";

export default {
  name: "HomePage",
  async setup() {
    const config = useRuntimeConfig();
    const mediaStore = useMediaStore();

    useHead(() => ({
      title: "PT Salam Pacific Indonesia Lines",
    }));

    if (!mediaStore.medias[1]) {
      await mediaStore.getMedia({ menu: 1 });
    }

    return { config };
  },
  components: { NumberAnimation, UseElementVisibility },
  data() {
    return {
      AboutUsImg,
      DataBgImg,
      HomeCtaImg,
      animationTimer: null,
      media_id: 1,
      overview: [
        {
          label: "homeDataPort",
          value: "39+",
        },
        {
          label: "homeDataRoute",
          value: "57+",
        },
        {
          label: "homeDataSentContainer",
          initial: 1000000,
          value: 9999999,
        },
        {
          label: "homeDataServing",
          initial: 1970,
          value: null,
        },
      ],
      services: [
        {
          label: "homeServicePTP",
          desc: "homeServicePTPDesc",
          icon: portToPortIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceDTD",
          desc: "homeServiceDTDDesc",
          icon: doorToDoorIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceAirfreight",
          desc: "homeServiceAirfreightDesc",
          icon: airFreightIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceLandTransport",
          desc: "homeServiceLandTransportDesc",
          icon: landTransportIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceLCL",
          desc: "homeServiceLCLDesc",
          icon: LCLIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceInternational",
          desc: "homeServiceInternationalDesc",
          icon: internationalIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceInsurance",
          desc: "homeServiceInsuranceDesc",
          icon: asuransiIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
        {
          label: "homeServiceWarehousing",
          desc: "homeServiceWarehousingDesc",
          icon: warehouseingIcon,
          alt: "shipping, shiplog, logistik, cargo, ekspedisi, kontainer, freight, SPIL",
        },
      ],
      carousel: [Campaign1, Campaign2, Campaign4, Campaign5],
      breakpoint: useDisplay(),

      // Carousel Slideshow state
      currentSlide: 0,
      loadedSlides: { 0: true, 1: true, 2: true },

      // FAQ Popup state
      faqDialog: false,
      faqSearch: "",
      faqLoading: false,
      faqActiveTopic: null,
      faqActiveArticle: null,
    };
  },
  watch: {
    currentSlide(newVal) {
      if (!this.sliders?.length) return;
      const total = this.sliders.length;
      this.loadedSlides = {
        ...this.loadedSlides,
        [newVal % total]: true,
        [(newVal + 1) % total]: true,
        [(newVal + 2) % total]: true,
        [(newVal - 1 + total) % total]: true,
      };
    },
    sliders: {
      handler(val) {
        if (val && val.length > 0) {
          const total = val.length;
          const current = this.currentSlide || 0;
          this.loadedSlides = {
            ...this.loadedSlides,
            [current % total]: true,
            [(current + 1) % total]: true,
            [(current + 2) % total]: true,
          };
        }
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState(useArticleStore, ["news_home"]),
    ...mapState(useMediaStore, ["medias", "sliders"]),
    ...mapState(useSocmedStore, ["socmed"]),
    ...mapState(useGuideStore, ["faqData", "faqTopic"]),

    mySPILURL() {
      return this.config.public.myspilUrl;
    },
  },
  methods: {
    ...mapActions(useGeneralStore, ["setActivePage"]),
    ...mapActions(useArticleStore, ["getNewsHome"]),
    ...mapActions(useMediaStore, [
      "getContent",
      "getMedia",
      "getSlider",
      "resetSlider",
    ]),
    ...mapActions(useSocmedStore, ["getSocmed"]),
    ...mapActions(useTrackAnalyticsStore, ["saveTrackAnalytics"]),
    ...mapActions(useGuideStore, ["getFaqNew2"]),

    async openFaqPopup() {
      this.faqDialog = true;
      this.faqActiveTopic = null;
      this.faqActiveArticle = null;

      if (!this.faqData || !this.faqTopic || this.faqTopic.length === 0) {
        this.faqLoading = true;
        await this.getFaqNew2({ search: "" });
        this.faqLoading = false;
      }
    },
    closeFaqPopup() {
      this.faqDialog = false;
      this.faqSearch = "";
      this.faqActiveTopic = null;
      this.faqActiveArticle = null;
    },
    async doFaqSearch() {
      this.faqActiveTopic = null;
      this.faqActiveArticle = null;
      this.faqLoading = true;
      await this.getFaqNew2({ search: this.faqSearch });
      this.faqLoading = false;

      // Track Analytics
      if (this.faqSearch) {
        const params = {
          action: "SEARCH FAQ",
          remark: "Home search FAQ myCustomer Service",
          val1: this.$route.path,
          val2: this.faqSearch,
          val3: null,
          val4: null,
          val5: null,
        };

        this.saveTrackAnalytics(params);
      }
    },
    selectFaqTopic(topic) {
      this.faqActiveTopic = topic;
      this.faqActiveArticle = null;
    },
    openFaqArticle(item) {
      this.faqActiveArticle = item;
    },

    playAnimation() {
      clearTimeout(this.animationTimer);
      this.animationTimer = setTimeout(() => {
        if (this.$refs.numberAnimation) {
          this.$refs.numberAnimation.forEach((na) => {
            na?.restart?.();
          });
        }
      }, 150);
    },
    handleNews(news) {
      const activePage = isIndonesia()
        ? `/id/berita/${news.slug_id}`
        : `/en/news/${news.slug_en}`;
      this.setActivePage(activePage);

      // Track Analytics
      const params = {
        action: "CLICK NEWS",
        remark: "Click News",
        val1: null,
        val2: null,
        val3: activePage,
        val4: null,
        val5: null,
      };

      this.saveTrackAnalytics(params);
    },

    nextSlider(idx) {
      return this.sliders[idx < this.sliders.length - 1 ? idx + 1 : 0];
    },
    isClickable(slider) {
      return slider?.source?.map((d) => d.id).includes(1) && slider?.url;
    },
    handleSliderLink(slider) {
      if (this.isClickable(slider)) {
        window.open(slider.url, "_blank", "noopener,noreferrer");
      }
    },
  },
  async mounted() {
    const fetchPromises = [];

    // Get news
    if (this.news_home.length == 0) {
      fetchPromises.push(this.getNewsHome());
    }

    // Get Header
    if (!this.medias[this.media_id]) {
      fetchPromises.push(this.getMedia({ menu: this.media_id }));
    }

    // Get Slider
    if (this.sliders && this.sliders?.length == 0) {
      fetchPromises.push(this.getSlider({ menu: this.media_id }));
    }

    // Get Social Media
    if (this.socmed && this.socmed?.length == 0) {
      fetchPromises.push(
        this.getSocmed({
          active: "1",
        }),
      );
    }

    await Promise.allSettled(fetchPromises);

    const date = new Date();
    this.overview[3].value = date.getFullYear();

    if (!this.faqTopic || this.faqTopic.length === 0) {
      this.getFaqNew2({ search: "" });
    }
  },
  unmounted() {
    this.resetSlider(this.media_id);
  },
};
</script>

<style scoped>
::-webkit-scrollbar {
  display: none;
}

.cta-image {
  width: 80%;
  height: auto;
  aspect-ratio: 2.19;
}
.image-carousel {
  width: 48%;
  height: auto;
  aspect-ratio: 4;
}
.about-container {
  flex-direction: row;
}
.ov-value {
  font-size: 4.5rem !important;
  line-height: 5.85rem !important;
}
.ov-bg {
  height: 560px;
}

@media screen and (max-width: 1279px) {
  .about-container {
    flex-direction: column-reverse;
  }
  .ov-value {
    font-size: 2.5rem !important;
    line-height: 3.25rem !important;
  }
}

@media screen and (max-width: 959px) {
  .image-carousel {
    width: 100%;
    margin-right: 0%;
  }
}

@media screen and (max-width: 599px) {
  .ov-value {
    font-size: 1.5rem !important;
    line-height: 2.1rem !important;
  }
  .data-title {
    text-align: center;
  }
  .ov-bg {
    height: 500px;
  }
  .service-container {
    text-align: center;
  }
}

/* ─── Responsive Layout Helpers ──────────────────────── */
.hero-text-container {
  width: 100%;
  padding-bottom: 18%;
}
.overview-item {
  width: 50%;
}

@media (min-width: 600px) {
  .hero-text-container {
    width: 50%;
    padding-bottom: 10%;
  }
  .overview-item {
    width: auto;
  }
}

@media (min-width: 1280px) {
  .hero-text-container {
    padding-bottom: 0;
  }
}

/* ─── FAQ Hero Button ─────────────────────────────────── */
.faq-hero-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 2px solid rgba(255, 255, 255, 1);
  color: #fff;
  font-weight: 400;
  padding: 8px 18px;
  cursor: pointer;
}
.faq-hero-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}
</style>
